import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { en } from './dictionaries/en';
import { ru } from './dictionaries/ru';

/**
 * A lightweight, dependency-free i18n layer (EN default, RU opt-in) - one
 * flat key -> string dictionary per language, a `t()` lookup with `{param}`
 * interpolation, and `Intl`-backed number/date/currency formatting. No
 * routing/URL changes, no page duplication: every page reads the SAME
 * component tree in both languages, only the rendered strings differ.
 *
 * `ru`'s type is constrained to exactly `en`'s key set (see dictionaries/ru.ts)
 * so a key present in one language and missing in the other is a compile
 * error, not a silent English/Russian mix at runtime.
 */

export type Locale = 'en' | 'ru';
const STORAGE_KEY = 'wms_ops_locale';
const DEFAULT_LOCALE: Locale = 'en';

const DICTIONARIES: Record<Locale, Record<string, string>> = { en, ru };

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'ru' ? 'ru' : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Storage may be unavailable (private mode) - the choice still works
    // for the lifetime of this tab via in-memory React state.
  }
}

export type TranslateFn = (key: keyof typeof en, params?: Readonly<Record<string, string | number>>) => string;

type LanguageContextValue = {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
  readonly t: TranslateFn;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

function interpolate(template: string, params?: Readonly<Record<string, string | number>>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in params ? String(params[key]) : match));
}

export function LanguageProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  const setLocale = useCallback((next: Locale): void => {
    persistLocale(next);
    setLocaleState(next);
  }, []);

  const t = useCallback<TranslateFn>(
    (key, params) => {
      const dict = DICTIONARIES[locale];
      const template = dict[key as string] ?? en[key];
      return interpolate(template, params);
    },
    [locale],
  );

  const value = useMemo<LanguageContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage() called outside <LanguageProvider>');
  return ctx;
}

/** Convenience hook for the common case of just needing `t`. */
export function useT(): TranslateFn {
  return useLanguage().t;
}
